import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type UserRole } from '@/lib/constants/roles';
import { cn } from '@/lib/utils/cn';
import {
  SIZE_DEFS,
  WIDGETS,
  type LayoutItem,
  type WidgetId,
  type WidgetSize,
} from '../config';
import { renderWidget } from '../widgets/registry';
import { WidgetCard } from './WidgetCard';

interface Props {
  layout: LayoutItem[];
  editing: boolean;
  onReorder: (fromId: WidgetId, toId: WidgetId) => void;
  onResize: (id: WidgetId, size: WidgetSize) => void;
  onRemove: (id: WidgetId) => void;
  role: UserRole;
  /**
   * Auto-fit mode: tiles flow into as many columns as fit the available
   * width, each at least `minTilePx` wide. Used by the KPI strip so the
   * 12-tile row stays readable on 1366px laptops without truncating
   * counts — at any width, every tile is at least minTilePx wide.
   */
  autoFit?: boolean;
  minTilePx?: number;
}

export function WidgetGrid({
  layout,
  editing,
  onReorder,
  onResize,
  onRemove,
  role,
  autoFit = false,
  minTilePx = 200,
}: Props) {
  const sensors = useSensors(
    // 4-px activation distance keeps clicks on the card body / buttons
    // from being interpreted as drag starts.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  if (layout.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card/50 py-16 text-center">
        <div className="text-sm font-semibold text-foreground">
          Nothing on this tab yet
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {editing
            ? 'Click "Add widget" to bring something in.'
            : 'Click "Customize" then "Add widget" to start.'}
        </p>
      </div>
    );
  }

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    onReorder(active.id as WidgetId, over.id as WidgetId);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={layout.map((l) => l.id)}
        strategy={rectSortingStrategy}
      >
        <div
          className={cn(
            'grid gap-3',
            autoFit
              ? // CSS Grid auto-fit: each track is min `minTilePx` wide,
                // max 1fr. Tiles flow into as many columns as fit. No
                // breakpoint cliffs.
                ''
              : 'grid-cols-1 md:grid-cols-6 lg:grid-cols-12',
          )}
          style={
            autoFit
              ? {
                  gridTemplateColumns: `repeat(auto-fit, minmax(${minTilePx}px, 1fr))`,
                }
              : undefined
          }
        >
          {layout.map((it) => (
            <SortableTile
              key={it.id}
              item={it}
              editing={editing}
              onResize={onResize}
              onRemove={onRemove}
              role={role}
              autoFit={autoFit}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

interface SortableTileProps {
  item: LayoutItem;
  editing: boolean;
  onResize: (id: WidgetId, size: WidgetSize) => void;
  onRemove: (id: WidgetId) => void;
  role: UserRole;
  autoFit?: boolean;
}

/** One sortable tile. CSS grid handles column span; the tile chooses its
 *  span via the SIZE_DEFS table so resize is a pure className swap. */
function SortableTile({
  item,
  editing,
  onResize,
  onRemove,
  role,
  autoFit = false,
}: SortableTileProps) {
  const desc = WIDGETS.find((w) => w.id === item.id);
  const def = SIZE_DEFS[item.size];

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: !editing,
  });

if (!desc || !desc.roles.includes(role)) return null;
  /*
   * Inline `gridColumn` style was overriding the `lg:col-span-N` Tailwind
   * class at lg breakpoint because inline styles beat class-based ones in
   * specificity — every tile ended up spanning `colsMd` columns even on
   * lg screens. Use Tailwind responsive classes for both breakpoints so
   * the lg span actually wins.
   */
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    minHeight: def.minHeight,
  };

  const colSpanMd: Record<number, string> = {
    1: 'md:col-span-1',
    2: 'md:col-span-2',
    3: 'md:col-span-3',
    4: 'md:col-span-4',
    5: 'md:col-span-5',
    6: 'md:col-span-6',
  };
  const colSpanLg: Record<number, string> = {
    1: 'lg:col-span-1',
    2: 'lg:col-span-2',
    3: 'lg:col-span-3',
    4: 'lg:col-span-4',
    5: 'lg:col-span-5',
    6: 'lg:col-span-6',
    7: 'lg:col-span-7',
    8: 'lg:col-span-8',
    9: 'lg:col-span-9',
    10: 'lg:col-span-10',
    11: 'lg:col-span-11',
    12: 'lg:col-span-12',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'min-w-0',
        // In autoFit mode the parent grid auto-flows tiles — skip
        // the explicit col-span classes so each tile takes one grid track.
        autoFit
          ? 'col-span-1'
          : cn(
              'col-span-1',
              colSpanMd[def.colsMd] ?? 'md:col-span-6',
              colSpanLg[def.colsLg] ?? 'lg:col-span-6',
            ),
        isDragging && 'z-10 opacity-80',
      )}
    >
      <WidgetCard
        title={desc.title}
        icon={desc.icon}
        editing={editing}
        onRemove={() => onRemove(item.id)}
        description={desc.description}
        dragAttributes={attributes}
        dragListeners={listeners}
        size={item.size}
        onSizeChange={(s) => onResize(item.id, s)}
        compact={desc.tab === 'kpi'}
      >
        {renderWidget(item.id, role)}
      </WidgetCard>
    </div>
  );
}
